<template>
    <div class="hero gap-9" :class="heroClass">
        <div class="hero-content" :class="heroContentClass">
            <slot name="header" :heading-text="headingText">
                <Heading
                    color="primary"
                    class="mb-2"
                    tag="h2"
                    size="web-xxx-lg"
                    style="letter-spacing: -1px;"
                >
                    {{ headingText }}
                </Heading>
            </slot>

            <slot name="content" />

            <slot name="footer" />
            <template v-if="!hideButton">
                <UButton
                    to="/contact?talk-to-an-expert=true"
                    color="primary"
                    variant="solid"
                    size="lg"
                    :label="btnText"
                    trailing-icon="i-pkg-navigation-arrow-right"
                    :ui="{ trailingIcon: 'size-6' }"
                />
            </template>
        </div>
        <div class="hero-image" :class="heroImageClass">
            <div class="aspect-square w-full max-w-hero-media-sm md:max-w-hero-media">
                <img
                    loading="eager"
                    :alt="imageAlt"
                    class="h-auto w-full object-contain"
                    width="1024"
                    height="1024"
                    fetchpriority="high"
                    :class="imageClass"
                    :src="$image(image) ?? undefined"
                >
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
    btnText?: string
    containerBorderRadius?: string
    containerClass?: string
    headingText?: string
    heroButtonClass?: string
    heroClass?: string
    heroContentClass?: string
    heroImageClass?: string
    hideButton?: boolean
    hideIcon?: boolean
    image?: string
    imageAlt?: string
    imageBorderRadius?: string
    imageClass?: string | Record<string, boolean>
    imageStyle?: Record<string, string>
    mainGap?: string | number
    uri?: string
}>(), {
    btnText: 'Talk to a sampling expert',
    containerBorderRadius: '0',
    containerClass: 'hero-image-container',
    headingText: 'Boost sales through targeted in-home product sampling',
    heroButtonClass: 'font-manrope',
    heroClass: 'pt-16 md:pt-0 pb-12 md:pb-6 px-2 md:px-0',
    heroContentClass: 'md:py-section-md px-0',
    heroImageClass: '',
    hideButton: false,
    hideIcon: false,
    image: 'new/main-home.png',
    imageAlt: 'product sampling',
    imageBorderRadius: '0',
    imageClass: '',
    imageStyle: () => ({}),
    mainGap: 36,
    uri: 'contact?talk-to-an-expert=true',
})
</script>

<style lang="scss" scoped>
.hero {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    flex-flow: column;

    &.gap-v2 {
        gap: 0px;
        @media (min-width: 1024px) {
            gap: 2rem;
        }
        @media (min-width: 1280px) {
            gap: 8.125rem;
        }
    }

    &-reverse {
        flex-flow: column-reverse !important;
    }

    &-content {
        width: 100%;
        max-width: 100%;
        @media (min-width: 768px) {
            max-width: 698px;
        }
        @media (min-width: 1024px) {
            flex: 1 1 56%;
            min-width: 0;
            max-width: none;
        }
    }

    &-image {
        display: flex;
        width: 100%;
        min-width: 0;
        justify-content: center;

        @media (min-width: 1024px) {
            flex: 0 1 44%;
        }

        &-container {
            width: 100%;
            height: 100%;
            background-repeat: no-repeat;
            background-size: contain;
            background-position: center;

            &-bg-cover {
                background-size: cover;
            }
        }
    }

    @media only screen and (min-width: 1024px) {
        flex-flow: row;
        justify-content: space-between;
    }
}
</style>
